import { useCallback, useEffect, useRef, useState } from "react";

export interface PollingState<T> {
  readonly data: T | null;
  readonly loading: boolean;
  readonly ready: boolean;
  readonly error: string | null;
  readonly refresh: () => void;
}

export function usePolling<T>(load: () => Promise<T>, intervalMs: number): PollingState<T> {
  const disabled = intervalMs <= 0;
  const loadRef = useRef(load);
  loadRef.current = load;

  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(!disabled);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inFlightRef = useRef(false);
  const requestGenRef = useRef(0);
  const hasLoadedRef = useRef(false);

  const runLoad = useCallback((silent: boolean) => {
    if (disabled || inFlightRef.current) {
      return;
    }

    const requestGen = ++requestGenRef.current;
    inFlightRef.current = true;
    if (!silent && !hasLoadedRef.current) {
      setLoading(true);
    }

    void loadRef
      .current()
      .then((next) => {
        if (requestGen !== requestGenRef.current) {
          return;
        }
        hasLoadedRef.current = true;
        setData(next);
        setError(null);
        setReady(true);
      })
      .catch((e: unknown) => {
        if (requestGen !== requestGenRef.current) {
          return;
        }
        setError(e instanceof Error ? e.message : String(e));
        setReady(true);
      })
      .finally(() => {
        if (requestGen === requestGenRef.current) {
          setLoading(false);
          inFlightRef.current = false;
        }
      });
  }, [disabled]);

  const runLoadRef = useRef(runLoad);
  runLoadRef.current = runLoad;

  const refresh = useCallback(() => {
    runLoadRef.current(hasLoadedRef.current);
  }, []);

  useEffect(() => {
    if (disabled) {
      requestGenRef.current += 1;
      setData(null);
      setError(null);
      setLoading(false);
      setReady(false);
      hasLoadedRef.current = false;
      inFlightRef.current = false;
      return;
    }

    runLoadRef.current(hasLoadedRef.current);

    let timer: ReturnType<typeof setInterval> | undefined;

    const start = () => {
      if (timer || document.visibilityState === "hidden") {
        return;
      }
      timer = setInterval(() => runLoadRef.current(true), intervalMs);
    };

    const stop = () => {
      if (timer) {
        clearInterval(timer);
        timer = undefined;
      }
    };

    const onVisibility = () => {
      if (document.visibilityState === "hidden") {
        stop();
      } else {
        start();
      }
    };

    document.addEventListener("visibilitychange", onVisibility);
    start();

    return () => {
      requestGenRef.current += 1;
      inFlightRef.current = false;
      document.removeEventListener("visibilitychange", onVisibility);
      stop();
    };
  }, [disabled, intervalMs]);

  return { data, loading, ready, error, refresh };
}
